import { z } from "zod";
import { eventSchema } from "../events";
import { ProductIdentifierAndTypeSchema, SubSchema } from "../paywall-js";
import { generateRandomHex } from "../tokens/bytes";
import { WebCheckoutDestination } from "../editor";
export const FeatureGating = {
    GATED: "GATED",
    NON_GATED: "NON_GATED"
};
export const ReRouteBackButton = {
    ENABLED: "ENABLED",
    DISABLED: "DISABLED"
};
export const GameControllerSupport = {
    ENABLED: "ENABLED",
    DISABLED: "DISABLED"
};
export const IntroductoryOfferEligibility = {
    AUTOMATIC: "AUTOMATIC",
    ALWAYS_ELIGIBLE: "ALWAYS_ELIGIBLE",
    ALWAYS_INELIGIBLE: "ALWAYS_INELIGIBLE"
};
export const OnDeviceCache = {
    ENABLED: "ENABLED",
    DISABLED: "DISABLED"
};
export const LocalizationType = {
    SIMPLE: "SIMPLE",
    ADVANCED: "ADVANCED"
};
export const IsScrollEnabled = {
    ENABLED: "ENABLED",
    DISABLED: "DISABLED"
};
export const PaywallDismissal = {
    NORMAL: "NORMAL",
    DISABLED: "DISABLED"
};
export const PaywallPresentationStyle = {
    MODAL: "MODAL",
    FULLSCREEN: "FULLSCREEN",
    PUSH: "PUSH",
    NO_ANIMATION: "NO_ANIMATION",
    DRAWER: "DRAWER"
};
export const PaywallPresentationStyleV3 = z.union([
    z.object({
        type: z.literal("DRAWER"),
        height: z.number(),
        corner_radius: z.number()
    }),
    z.object({
        type: z.literal("POPUP"),
        width: z.number(),
        height: z.number(),
        corner_radius: z.number()
    }),
    z.object({
        type: z.literal("MODAL")
    }),
    z.object({
        type: z.literal("FULLSCREEN")
    }),
    z.object({
        type: z.literal("PUSH")
    }),
    z.object({
        type: z.literal("NO_ANIMATION")
    })
]);
export const ShowPaywallOption = {
    EXPLICIT: "EXPLICIT",
    ON_FIRST_LAUNCH: "ON_FIRST_LAUNCH",
    ON_NTH_LAUNCH: "ON_NTH_LAUNCH"
};
export const SurveyPresentationCondition = {
    ON_MANUAL_CLOSE: "ON_MANUAL_CLOSE",
    ON_PURCHASE: "ON_PURCHASE"
};
export const ApplicationUserProperites = z.object({
    email: z.string().optional(),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    apns_token: z.string().optional(),
    fcm_token: z.string().optional()
});
export const CustomStringProperty = z.object({
    key: z.string(),
    value: z.string(),
    type: z.literal("string")
});
export const CustomIntegerProperty = z.object({
    key: z.string(),
    value: z.number().int(),
    type: z.literal("integer")
});
export const CustomProperty = z.union([
    CustomIntegerProperty,
    CustomStringProperty
]);
const PaywallRequestPresent = z.object({
    type: z.literal("present").default("present")
});
const PaywallRequestIdentifier = z.object({
    type: z.literal("identifier").default("identifier"),
    identifier: z.string()
});
const PaywallRequestEvent = z.object({
    type: z.literal("event").default("event"),
    event: eventSchema
});
export const PaywallRequest = z.union([
    PaywallRequestIdentifier,
    PaywallRequestEvent,
    PaywallRequestPresent
]);
export const ComputedPropertySchema = z.object({
    type: z.enum([
        "MINUTES_SINCE",
        "DAYS_SINCE",
        "HOURS_SINCE",
        "MONTHS_SINCE",
        "YEARS_SINCE"
    ]),
    event_name: z.string()
});
export const ComputedPropertiesSchema = z.array(ComputedPropertySchema);
export const LocalNotificationSchema = z.object({
    type: z.enum([
        "TRIAL_STARTED"
    ]),
    title: z.string(),
    subtitle: z.string().optional(),
    body: z.string(),
    // milliseconds
    delay: z.number()
});
export const LocalNotificationsSchema = z.array(LocalNotificationSchema);
export const IgnoreLocalizationsSchema = z.array(z.string());
export const SurveySchema = z.object({
    id: z.string(),
    assignment_key: z.string(),
    title: z.string(),
    message: z.string(),
    options: z.array(z.object({
        id: z.string(),
        title: z.string()
    })),
    presentation_probability: z.number(),
    locale: z.string(),
    include_other_option: z.boolean(),
    include_close_option: z.boolean()
});
export const SurveySchemaWithPresentationCondition = SurveySchema.extend({
    presentation_condition: z.nativeEnum(SurveyPresentationCondition)
});
// AMAZON, APP_STORE, MAC_APP_STORE, PLAY_STORE, PROMOTIONAL, STRIPE.
export const AppStoreProduct = z.object({
    store: z.literal("APP_STORE"),
    product_identifier: z.string()
});
export const PlayStoreProduct = z.object({
    store: z.literal("PLAY_STORE"),
    product_identifier: z.string(),
    base_plan_identifier: z.string(),
    offer: z.discriminatedUnion("type", [
        z.object({
            type: z.literal("AUTOMATIC")
        }),
        z.object({
            type: z.literal("SPECIFIED"),
            offer_identifier: z.string()
        })
    ])
});
export const StripeProduct = z.object({
    store: z.literal("STRIPE"),
    environment: z.union([
        z.literal("live"),
        z.literal("test")
    ]),
    product_identifier: z.string(),
    trial_days: z.number().nullable()
});
export const PaddleProduct = z.object({
    store: z.literal("PADDLE"),
    environment: z.union([
        z.literal("live"),
        z.literal("test")
    ]),
    product_identifier: z.string()
});
export const entitlmentTypeSchema = z.enum([
    "SERVICE_LEVEL"
]);
export const EntitlmentSchema = z.object({
    identifier: z.string(),
    type: entitlmentTypeSchema
});
export const StoreProduct = z.discriminatedUnion("store", [
    AppStoreProduct,
    PlayStoreProduct,
    StripeProduct,
    PaddleProduct
]);
export const StoreProductWithName = z.object({
    // This is the actual unique identifier for the product in the context of
    // superwall
    // In iOS it's just the product identifier, in Android it's the composited
    // product id, base plan, and offer. On Stripe it's the price identifier
    // environment and trial
    sw_composite_product_id: z.string(),
    reference_name: z.string(),
    store_product: StoreProduct,
    entitlements: z.array(EntitlmentSchema)
});
export const PaywallResponseSchema = z.object({
    id: z.string(),
    // The id of the build which produced this paywall response artifact
    build_id: z.string(),
    // Used to cache the paywall on the client.
    // If this key changes, then the paywall needs to be reloaded
    cache_key: z.string(),
    url: z.string(),
    url_config: z.object({
        endpoints: z.array(z.object({
            url: z.string(),
            percentage: z.number(),
            timeout_ms: z.number()
        })),
        max_attempts: z.number()
    }),
    name: z.string(),
    identifier: z.string(),
    slug: z.string(),
    substitutions: z.array(SubSchema),
    presentation_style: z.union([
        z.literal("MODAL"),
        z.literal("FULLSCREEN")
    ]),
    presentation_style_v2: z.nativeEnum(PaywallPresentationStyle),
    presentation_style_v3: PaywallPresentationStyleV3,
    presentation_condition: z.union([
        z.literal("ALWAYS"),
        z.literal("CHECK_USER_SUBSCRIPTION")
    ]),
    presentation_delay: z.number(),
    manifest: z.object({
        // cache: z.enum(["FOREVER", "NEVER", "SESSION"]),
        use: z.enum([
            "NEVER",
            "IF_AVAILABLE_ON_PAYWALL_OPEN",
            "ALWAYS"
        ]),
        document: z.object({
            url: z.string(),
            mime_type: z.string()
        }),
        resources: z.array(z.object({
            url: z.string(),
            mime_type: z.string()
        }))
    }).optional(),
    background_color_hex: z.string(),
    dark_background_color_hex: z.string().nullable(),
    feature_gating: z.nativeEnum(FeatureGating),
    reroute_back_button: z.nativeEnum(ReRouteBackButton),
    game_controller_support: z.nativeEnum(GameControllerSupport),
    introductory_offer_eligibility: z.nativeEnum(IntroductoryOfferEligibility).default("AUTOMATIC"),
    web_checkout_destination: z.enum(WebCheckoutDestination).nullable(),
    on_device_cache: z.nativeEnum(OnDeviceCache),
    is_scroll_enabled: z.boolean(),
    launch_option: z.nativeEnum(ShowPaywallOption),
    dismissal_option: z.nativeEnum(PaywallDismissal),
    computed_properties: ComputedPropertiesSchema,
    local_notifications: LocalNotificationsSchema,
    products_v2: z.array(StoreProductWithName),
    products: z.array(ProductIdentifierAndTypeSchema.extend({
        // Duplicate productId for forward compatibility with android
        product_id: z.string(),
        product_id_android: z.string()
    })),
    paywalljs_event: z.string(),
    survey: SurveySchema.optional(),
    surveys: z.array(SurveySchemaWithPresentationCondition)
});
export const toSlug = (name)=>{
    return name.toLowerCase().replace(/ /g, "-").replace(/[^\w-]+/g, "");
};
export const createPaywallSlug = async (name)=>{
    return `${toSlug(name)}-${await generateRandomHex(2)}-${new Date().toISOString().split("T")[0]}`;
};
export const ApplicationPlatform = z.enum([
    "IOS",
    "ANDROID",
    "STRIPE",
    "WEB",
    "PADDLE",
    "PROMOTIONAL"
]).optional();
